/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { X, Mic, MicOff, Sparkles, Loader2, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  saveTranscriptChunk, getTranscript, generateSoapFromTranscript,
} from '@/lib/aiTriage';

/**
 * LiveTranscriptPanel — real-time clinical transcription for the video room.
 *
 * Uses the browser's Web Speech API (SpeechRecognition) to transcribe locally,
 * sends final chunks to the backend, and can ask Claude to generate SOAP notes
 * from the cumulative transcript.
 *
 * Only rendered for the doctor (professional side of the call).
 */
const LiveTranscriptPanel = ({ apptId, speakerRole = 'doctor', onClose }) => {
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const [chunks, setChunks] = useState([]); // {id, text, speaker, created_at}
  const [interim, setInterim] = useState('');
  const [busy, setBusy] = useState(false);
  const [soap, setSoap] = useState(null);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false);
  const scrollRef = useRef(null);

  // Load pre-existing chunks
  const loadTranscript = useCallback(async () => {
    try {
      const res = await getTranscript(apptId);
      setChunks(res.chunks || []);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.debug('[LiveTranscript] load failed', err);
    }
  }, [apptId]);

  useEffect(() => { loadTranscript(); }, [loadTranscript]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chunks.length, interim]);

  // Init SpeechRecognition
  useEffect(() => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'it-IT';
    rec.maxAlternatives = 1;

    rec.onresult = async (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const t = res[0]?.transcript || '';
        if (res.isFinal) finalText += t;
        else interimText += t;
      }
      if (interimText) setInterim(interimText);
      if (finalText) {
        setInterim('');
        try {
          const clean = finalText.trim();
          if (!clean) return;
          const res = await saveTranscriptChunk({
            appointment_id: apptId,
            text: clean,
            speaker: speakerRole,
            is_final: true,
          });
          if (res?.chunk) setChunks((c) => [...c, res.chunk]);
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.debug('[LiveTranscript] save failed', err);
        }
      }
    };

    rec.onerror = (e) => {
      if (process.env.NODE_ENV !== 'production') console.debug('[LiveTranscript] recognition error', e);
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        toast.error('Permesso microfono negato');
        shouldRestartRef.current = false;
        setListening(false);
      }
    };

    rec.onend = () => {
      if (shouldRestartRef.current) {
        try { rec.start(); } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.debug('[LiveTranscript] restart failed', err);
        }
      } else {
        setListening(false);
      }
    };

    recognitionRef.current = rec;
    return () => {
      shouldRestartRef.current = false;
      try { rec.stop(); } catch { /* noop */ }
      recognitionRef.current = null;
    };
  }, [apptId, speakerRole]);

  const startListening = () => {
    if (!recognitionRef.current) return;
    shouldRestartRef.current = true;
    try {
      recognitionRef.current.start();
      setListening(true);
    } catch (err) {
      // Already started — mark as listening
      setListening(true);
    }
  };
  const stopListening = () => {
    if (!recognitionRef.current) return;
    shouldRestartRef.current = false;
    try { recognitionRef.current.stop(); } catch { /* noop */ }
    setListening(false);
  };

  const generateSoap = async () => {
    if (busy) return;
    setBusy(true);
    setSoap(null);
    try {
      const res = await generateSoapFromTranscript(apptId);
      setSoap(res.soap);
      toast.success('Note SOAP generate dall\'AI');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossibile generare le note SOAP');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
      className="absolute top-0 right-0 h-full w-full sm:w-[440px] glass-strong border-l border-border/60 flex flex-col z-30"
      data-testid="live-transcript-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="font-display font-semibold">Trascrizione AI</div>
          {listening && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> Live
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="live-transcript-close-btn"><X className="h-4 w-4" /></Button>
      </div>

      <div className="px-4 py-3 border-b border-border/60 space-y-2">
        {!supported ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs p-2" data-testid="live-transcript-unsupported">
            Il browser non supporta la trascrizione vocale (Web Speech API). Prova con Google Chrome desktop.
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {listening ? (
              <Button variant="secondary" onClick={stopListening} className="flex-1" data-testid="live-transcript-stop-btn">
                <MicOff className="h-4 w-4" /> Pausa
              </Button>
            ) : (
              <Button onClick={startListening} className="flex-1" data-testid="live-transcript-start-btn">
                <Mic className="h-4 w-4" /> Avvia trascrizione
              </Button>
            )}
            <Button variant="outline" onClick={generateSoap} disabled={busy || chunks.length === 0} data-testid="live-transcript-generate-soap-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              SOAP
            </Button>
          </div>
        )}
        <div className="text-[11px] text-muted-foreground">
          La trascrizione avviene <b>nel browser</b> (nessun audio inviato al server). I chunk testuali finali vengono salvati collegati alla visita.
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-2 text-sm" data-testid="live-transcript-chunks">
        {chunks.length === 0 && !interim && (
          <div className="text-center text-xs text-muted-foreground py-6">
            Nessuna trascrizione ancora. Clicca “Avvia trascrizione” per iniziare.
          </div>
        )}
        {chunks.map((c) => (
          <div key={c.id} className={cn('rounded-lg p-2', c.speaker === 'doctor' ? 'bg-primary/5 border border-primary/10' : 'bg-muted/50 border border-border/40')} data-testid={`transcript-chunk-${c.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{c.speaker === 'doctor' ? 'Medico' : c.speaker === 'patient' ? 'Paziente' : 'Voce'}</Badge>
              <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleTimeString('it')}</span>
            </div>
            <p className="whitespace-pre-wrap leading-relaxed">{c.text}</p>
          </div>
        ))}
        {interim && (
          <div className="rounded-lg p-2 border border-dashed border-primary/40 bg-primary/5 text-muted-foreground italic" data-testid="transcript-interim">
            {interim}…
          </div>
        )}

        {soap && (
          <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="transcript-soap-result">
            <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wider">
              <Sparkles className="h-3 w-3" /> Note SOAP generate
            </div>
            {['subjective', 'objective', 'assessment', 'plan'].map((k) => (
              soap[k] ? (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{k.toUpperCase()}</div>
                  <p className="text-sm whitespace-pre-wrap">{soap[k]}</p>
                </div>
              ) : null
            ))}
            {soap.highlights?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Punti chiave</div>
                <ul className="text-sm list-disc pl-5">
                  {soap.highlights.map((h, i) => <li key={`hl-${i}-${(h || '').slice(0, 20)}`}>{h}</li>)}
                </ul>
              </div>
            )}
            <p className="text-[10px] italic text-muted-foreground pt-2 border-t border-border/40">{soap.disclaimer}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default LiveTranscriptPanel;
